import React from "react";
import * as Icon from "react-feather";
import AFSService from "../../../../../../services/AFSService";
import CheckAgainButton from "../../shared/CheckAgainButton";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import UtilityService from "../../../../../../services/UtilityService";
import WComponent from "../../../../../common/WComponent";
import "./DocumentationView.css";

class DocumentationView extends WComponent {
	state = { checklist: [], loading: true, uploadingCode: null, openCodes: {} };

	// Holds the per-row hidden <input type=file> DOM nodes, keyed by code.
	fileInputs = {};

	componentDidMount() {
		this.loadChecklist();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.clientId !== this.props.clientId) {
			this.loadChecklist();
		}
	}


	toggleRow = code => {
		const openCodes = { ...this.state.openCodes };
		if (openCodes[code]) delete openCodes[code];
		else openCodes[code] = true;
		this.update({ openCodes });
	};

	renderDetail = item => {
		const document = item.document;
		const accountNames = (item.accounts || []).map(account => account.display_name).join(", ");

		if (!document) {
			return (
				<div className="documentation__detail">
					<div className="documentation__detail-empty">
						Nothing uploaded yet{item.required ? " — this one is required before the statements can be finalised." : "."}
					</div>
					{accountNames ? <div className="documentation__detail-accounts">Supports: {accountNames}</div> : null}
				</div>
			);
		}

		const verification = document.verification;
		const verdict = verification ? verification.verdict : null;
		const uploadedOn = document.uploaded_at ? new Date(document.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

		return (
			<div className="documentation__detail">
				<div className="documentation__detail-file">
					<span className="documentation__detail-file-name">{document.name}</span>
					{uploadedOn ? <span className="documentation__detail-file-date">uploaded {uploadedOn}</span> : null}
				</div>

				{verification && verification.narration ? (
					<div className="documentation__detail-narration">{verification.narration}</div>
				) : null}

				{verification && !verification.narration && verification.error_detail ? (
					<div className="documentation__detail-narration documentation__detail-narration--exception">
						{verification.error_detail}
					</div>
				) : null}

				{verification ? (
					<div className="documentation__detail-facts">
						<div className="documentation__detail-fact">
							<span className="documentation__detail-fact-label">Checked</span>
							<span className={"documentation__detail-fact-value documentation__detail-fact-value--" + verdict.tone}>{verdict.text}</span>
						</div>
						{verification.extracted_balance !== null && verification.extracted_balance !== undefined ? (
							<div className="documentation__detail-fact">
								<span className="documentation__detail-fact-label">Closing balance on the document</span>
								<span className="documentation__detail-fact-value">{UtilityService.formatAccountingAmount(verification.extracted_balance)}</span>
							</div>
						) : null}
						{verification.expected_balance !== null && verification.expected_balance !== undefined ? (
							<div className="documentation__detail-fact">
								<span className="documentation__detail-fact-label">Balance in the books</span>
								<span className="documentation__detail-fact-value">{UtilityService.formatAccountingAmount(verification.expected_balance)}</span>
							</div>
						) : null}
						{verification.extracted_date ? (
							<div className="documentation__detail-fact">
								<span className="documentation__detail-fact-label">Statement date</span>
								<span className="documentation__detail-fact-value">{verification.extracted_date}</span>
							</div>
						) : null}
						{verification.confidence ? (
							<div className="documentation__detail-fact">
								<span className="documentation__detail-fact-label">Confidence</span>
								<span className="documentation__detail-fact-value">{verification.confidence}</span>
							</div>
						) : null}
					</div>
				) : null}

				{verification && verification.what_to_do_next ? (
					<div className="documentation__detail-next">{verification.what_to_do_next}</div>
				) : null}

				{verification && verification.can_check_again ? (
					<CheckAgainButton documentId={document.id} onChecked={this.loadChecklist} className="documentation__detail-recheck" />
				) : null}

				{accountNames ? <div className="documentation__detail-accounts">Supports: {accountNames}</div> : null}
			</div>
		);
	};

	loadChecklist = () => {
		AFSService.fetchDocumentationChecklist({ clientId: this.props.clientId }).then((data) => {
			this.update({ checklist: data.checklist || [], loading: false });
		}).catch(() => {
			this.update({ loading: false });
		});
	};

	handleUpload = async (item, file) => {
		if (!file) return;
		this.update({ uploadingCode: item.code });
		try {
			await BookkeepingService.uploadLeadSheetDocument({
				clientId: this.props.clientId,
				wpRef: item.wp_ref,
				evidenceRequirementCode: item.code,
				canonicalCode: (item.accounts && item.accounts[0]) ? item.accounts[0].canonical_code : null,
				expectedBalance: (item.accounts && item.accounts[0]) ? item.accounts[0].balance : null,
				file: file,
			});
			this.loadChecklist();
		} catch (err) {
			console.error("Upload failed:", err);
		}
		this.update({ uploadingCode: null });
	};

	triggerUpload = (code) => {
		if (this.fileInputs[code]) this.fileInputs[code].click();
	};

	render() {
		let { checklist, loading, uploadingCode } = this.state;

		if (loading) {
			return (
				<div className="documentation">
					<div className="documentation__loading">
						Loading documentation requirements...
					</div>
				</div>
			);
		}

		let uploadedCount = checklist.filter(function (item) { return item.document; }).length;
		let requiredMissing = checklist.filter(function (item) { return item.required && !item.document && !item.waived; }).length;

		return (
			<div className="documentation">
				<div className="documentation__header">
					<span className="documentation__title">Supporting Documentation</span>
					<div className="documentation__summary">
						<span className="documentation__summary-stat documentation__summary-stat--complete">
							<Icon.CheckCircle size={13} />
							{uploadedCount} uploaded
						</span>
						{requiredMissing > 0 && (
							<span className="documentation__summary-stat documentation__summary-stat--missing">
								<Icon.AlertCircle size={13} />
								{requiredMissing} required missing
							</span>
						)}
						<span className="documentation__summary-stat">
							{checklist.length} total
						</span>
					</div>
				</div>

				<div className="documentation__list">
					{checklist.map((item, i) => {
						let hasDoc = !!item.document;
						let isMissing = item.required && !hasDoc && !item.waived;

						let iconClass = hasDoc
							? "documentation__item-icon--uploaded"
							: item.required
								? "documentation__item-icon--missing"
								: "documentation__item-icon--optional";

						let badgeClass = hasDoc
							? "documentation__item-badge--uploaded"
							: item.required
								? "documentation__item-badge--required"
								: "documentation__item-badge--optional";

						let badgeText = hasDoc
							? "Uploaded"
							: item.waived
								? "Waived"
								: item.required
									? "Required"
									: "Optional";

						let accountNames = (item.accounts || [])
							.slice(0, 3)
							.map(function (a) { return a.display_name; })
							.join(", ");
						let moreCount = (item.accounts || []).length > 3 ? " +" + ((item.accounts || []).length - 3) + " more" : "";

						let isUploading = uploadingCode === item.code;
						let isOpen = !!this.state.openCodes[item.code];
						let verdict = hasDoc && item.document.verification ? item.document.verification.verdict : null;

						return (
							<div key={i} className="documentation__row">
								<div
									className={"documentation__item documentation__item--expandable" + (isOpen ? " documentation__item--open" : "")}
									onClick={() => this.toggleRow(item.code)}
								>
									<span className="documentation__disclosure">
										{isOpen ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
									</span>
									<div className={"documentation__item-icon " + iconClass}>
										{hasDoc ? <Icon.Check size={12} /> : isMissing ? <Icon.AlertCircle size={12} /> : <Icon.Circle size={12} />}
									</div>
									<div className="documentation__item-content">
										<div className="documentation__item-label">{item.label}</div>
										<div className="documentation__item-meta">
											{accountNames}{moreCount}
											{verdict && (
												<span className={"documentation__verdict documentation__verdict--" + verdict.tone}>
													{verdict.tone === "agreed" ? <Icon.Check size={11} /> : verdict.tone === "unchecked" ? <Icon.Clock size={11} /> : verdict.tone === "caution" ? <Icon.AlertTriangle size={11} /> : <Icon.AlertCircle size={11} />}
													{verdict.text}
												</span>
											)}
										</div>
									</div>
									<span className={"documentation__item-badge " + badgeClass}>{badgeText}</span>
									<span className="documentation__item-section">{item.section_title}</span>
									<div className="documentation__item-upload" onClick={event => event.stopPropagation()}>
										<input
											type="file"
											ref={(el) => { this.fileInputs[item.code] = el; }}
											className="documentation__file-input-hidden"
											onChange={(e) => { if (e.target.files[0]) this.handleUpload(item, e.target.files[0]); e.target.value = ""; }}
										/>
										<button
											type="button"
											className="tk-button tk-button--secondary"
											onClick={() => this.triggerUpload(item.code)}
											disabled={isUploading}
										>
											{isUploading ? "Uploading…" : hasDoc ? "Replace" : "Upload"}
										</button>
									</div>
								</div>
								{isOpen ? this.renderDetail(item) : null}
							</div>
						);
					})}
					{checklist.length === 0 && (
						<div className="documentation__loading">
							No documentation requirements for this engagement
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default DocumentationView;
